/** Indicador compartilhado: preserva o modelo visual de Produtos/Serviços do CRM. */
import { ArrowUpRight, CircleHelp, TrendingDown, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { PREMIUM_DASHBOARD_CARD } from './dashboardCardStyles';

interface DashboardMetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  tooltip?: string;
  delta?: number | null;
  isLoading?: boolean;
  onClick?: () => void;
  className?: string;
}

/** O domínio fornece valores e ações; o cartão compartilha apenas a apresentação. */
export function DashboardMetricCard({ title, value, subtitle, tooltip, delta, isLoading, onClick, className }: DashboardMetricCardProps) {
  return (
    <Card
      className={cn(PREMIUM_DASHBOARD_CARD, 'min-w-0', onClick && 'cursor-pointer ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', className)}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && onClick && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onClick();
        }
      }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardContent className="space-y-1 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <p className="text-xs font-medium text-muted-foreground">{title}</p>
            {/* A ajuda não propaga o clique para o comando do indicador. */}
            {tooltip && <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0 text-muted-foreground" aria-label={`Informações sobre ${title}`} onClick={event => event.stopPropagation()}>
                <CircleHelp className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </TooltipTrigger><TooltipContent className="max-w-xs">{tooltip}</TooltipContent></Tooltip></TooltipProvider>}
          </div>
          {onClick && <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />}
        </div>
        {isLoading ? <Skeleton className="h-7 w-24 max-w-full" /> : <p className="truncate text-xl font-semibold text-foreground" title={String(value)}>{value}</p>}
        {(subtitle || (delta !== null && delta !== undefined)) && <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {delta !== null && delta !== undefined && (
            <span className={cn('inline-flex items-center gap-0.5 font-medium', delta >= 0 ? 'text-success' : 'text-destructive')}>
              {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(Math.abs(delta))}%
            </span>
          )}
          {subtitle && <span className="truncate" title={subtitle}>{subtitle}</span>}
        </div>}
      </CardContent>
    </Card>
  );
}